import type {
  QualifyingStatusProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { useGetSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import {
  allowDiscountsOnRequiredProducts,
  buildFilter,
  getDiscountLabel,
  getDiscountedWithoutSameQualified,
  getPriceIdsForStoreSpecial,
} from '@iheartjane/menu';
import { useEffect } from 'react';

import { getStoreSpecialStatusText } from '@jane/kiosk/util';
import { AVAILABLE_WEIGHTS } from '@jane/search/util';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial as tStoreSpecial } from '@jane/shared/models';
import type { Store, StoreSpecial } from '@jane/shared/types';

import {
  FilterAndSort,
  MenuProducts,
  SpecialDetail,
  getColumnCount,
} from '../../components';
import {
  useGetCart,
  useRemoveCartProduct,
  useUpdateCartProduct,
} from '../../data-access';
import { useMenuProductSearch, useProductTracking } from '../../providers';

interface SpecialInnerProps {
  navigateToCart: () => void;
  navigateToMenu: () => void;
  store: Store;
  storeSpecial: StoreSpecial | tStoreSpecial | V3StoreSpecial;
}

export const SpecialInner = ({
  navigateToCart,
  navigateToMenu,
  store,
  storeSpecial,
}: SpecialInnerProps) => {
  const { data: cart } = useGetCart();
  const { data: qualifyingStatusData, refetch: fetchStatus } =
    useGetSpecialQualifyingStatus({
      cartUuid: cart?.uuid,
      specialId: storeSpecial?.id,
      storeId: store?.id,
    });
  const qualifyingStatus = qualifyingStatusData?.qualifying_status;

  const { mutate: removeCartProduct } = useRemoveCartProduct({});
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const {
    cartToppers: { removeCartTopperId },
    inlineAds: { removeInlineAdId },
  } = useProductTracking();

  const handleRemoveProduct = (product: QualifyingStatusProduct) => {
    const cartProduct = cart?.products.find(
      ({ id, price_id }) => id === product.id && price_id === product.price_id
    );

    if (!cartProduct) return;

    if (cartProduct.count === 1) {
      removeCartProduct(
        {
          cart_uuid: cart?.uuid ?? null,
          product_id: cartProduct.id,
        },
        {
          onSuccess: () => {
            removeInlineAdId(cartProduct.id);
            removeCartTopperId(cartProduct.id);
          },
        }
      );
    } else {
      updateProduct({
        cart_uuid: String(cart?.uuid),
        product: {
          count: cartProduct.count - 1,
          price_id: cartProduct.price_id,
          product_id: cartProduct.product_id,
        },
      });
    }
  };

  const { calloutText, reachedMaxDiscountables, remainingQualifiers } =
    getStoreSpecialStatusText({
      currentSpecial: storeSpecial as StoreSpecial,
      qualifyingStatus: qualifyingStatusData,
    });

  const discountedWithoutSameQualified =
    getDiscountedWithoutSameQualified(qualifyingStatusData);

  const allowQualifiersToBeDiscounted =
    allowDiscountsOnRequiredProducts(storeSpecial);
  const discountedProductsToShow = allowQualifiersToBeDiscounted
    ? discountedWithoutSameQualified
    : qualifyingStatusData?.discounted_products;

  const isSpendingThreshold =
    storeSpecial.special_type === 'spending_threshold';
  const showBundleProgress =
    storeSpecial?.special_type === 'bundle' || isSpendingThreshold;

  const applicablePriceIds = getPriceIdsForStoreSpecial(
    storeSpecial,
    qualifyingStatus ?? 'not_qualified'
  );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds?.length &&
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  // if only some weights are returned, we want to filter by weight
  const availableWeightsFilter = visiblePriceIds
    ? buildFilter(
        'available_weights',
        visiblePriceIds.map((priceId) => priceId.split('_').join(' '))
      )
    : '';

  const defaultSortName = store?.optional_filters ? 'Featured' : null;

  const productsQuery = useMenuProductSearch({
    category: 'all',
    columnCount: getColumnCount(),
    defaultSortName,
    featuredCategory: availableWeightsFilter,
    isEnabled: !!storeSpecial,
    qualifyingStatus: qualifyingStatusData?.qualifying_status,
    store,
    storeSpecial: storeSpecial,
  });

  // sets the correct top value so the sticky headers group together
  const pageHeaderHeight =
    // eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc
    window.document.getElementById('store-special-page-header')?.clientHeight ||
    0;

  useEffect(() => {
    fetchStatus();
  }, [cart?.uuid, cart?.products, fetchStatus]);

  return (
    <SpecialDetail>
      <SpecialDetail.Layout top={pageHeaderHeight}>
        <SpecialDetail.Header
          specialDescription={storeSpecial.description ?? ''}
          specialTitle={storeSpecial.title}
          specialTerms={storeSpecial.terms ?? undefined}
        >
          {showBundleProgress && (
            <SpecialDetail.Progress
              allowQualifiersToBeDiscounted={allowQualifiersToBeDiscounted}
              discountedProducts={
                (discountedProductsToShow as QualifyingStatusProduct[]) || []
              }
              onRemoveProduct={(product) => handleRemoveProduct(product)}
              qualifyBySpendAmount={isSpendingThreshold}
              qualifiedProducts={
                (qualifyingStatusData?.qualified_products as QualifyingStatusProduct[]) ||
                []
              }
              qualifiedProductsTotal={qualifyingStatusData?.product_price}
              qualifyingStatus={qualifyingStatus || 'not_qualified'}
              qualifyingTargetAmount={
                qualifyingStatusData?.qualifying_threshold
              }
              reachedMaxDiscountables={reachedMaxDiscountables}
              remainingQualifiers={
                isSpendingThreshold
                  ? !!qualifyingStatusData?.qualified_products &&
                    qualifyingStatusData.qualified_products.length > 0
                    ? 0
                    : 1
                  : remainingQualifiers
              }
              tagLabel={getDiscountLabel({
                discountAmount: storeSpecial.discount_amount,
                discountType: storeSpecial.discount_type,
                specialType: storeSpecial.special_type,
                storeComplianceLanguageSettings:
                  store?.store_compliance_language_settings,
              })}
            />
          )}
        </SpecialDetail.Header>

        {showBundleProgress && (
          <>
            <SpecialDetail.Callout calloutText={calloutText} />

            <SpecialDetail.Footer
              onCartClick={navigateToCart}
              onShopMoreClick={navigateToMenu}
              qualifyingStatus={qualifyingStatus || 'not_qualified'}
            />
          </>
        )}
      </SpecialDetail.Layout>

      <FilterAndSort
        condensedView
        defaultSortName={defaultSortName}
        optionalFilters={store.optional_filters}
        searchProps={productsQuery}
      />
      <MenuProducts
        category="specials"
        searchProps={productsQuery}
        showProductCount={false}
        visiblePriceIds={visiblePriceIds ?? undefined}
      />
    </SpecialDetail>
  );
};
